import { type WalletLedger, type WalletWithTotalAmountDTO } from '@capsicum/common';
import { useMainCurrenciesQuery, useWalletFormatter, useWalletQuery } from '@capsicum/socios-core';
import { useCallback } from 'react';

import { type WalletMainCurrencies } from '../models';

/**
 *
 * @returns default and alternative currencies (eg. CHZ and EUR) and their balances
 */
export const useWalletCurrencies = () => {
	const { formatBalance } = useWalletFormatter();
	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};

	const walletCurrenciesReducer = useCallback(
		(data: undefined | WalletWithTotalAmountDTO) =>
			data?.ledgers?.reduce(
				(acc, ledger: WalletLedger): WalletMainCurrencies => {
					const isDefaultCurrencyLedger = ledger?.currency?.id === defaultCurrency?.id;

					if (!isDefaultCurrencyLedger) {
						return acc;
					}

					return {
						altCurrency: {
							...altCurrency,
							rawBalance: Number(ledger.balance),
							rawLockedBalance: Number(ledger.lockedBalance),
							...formatBalance(altCurrency, ledger)
						},
						defaultCurrency: {
							rawBalance: Number(ledger.balance),
							rawLockedBalance: Number(ledger.lockedBalance),
							...defaultCurrency,
							...formatBalance(defaultCurrency, ledger)
						},
						regulation: data.regulation
					};
				},
				{
					altCurrency: {},
					defaultCurrency: {},
					regulation: ''
				} as WalletMainCurrencies
			),
		[defaultCurrency, altCurrency, formatBalance]
	);

	return useWalletQuery(walletCurrenciesReducer);
};
